/**
 * Custom hook for handling section navigation (scroll, hash changes, intersection observer)
 */

import { useEffect, useState, RefObject } from 'react';
import { scrollToSection } from '../utils/scrollUtils';

export interface UseSectionNavigationOptions {
  overviewRef: RefObject<HTMLDivElement>;
  companyRef: RefObject<HTMLDivElement>;
  jobDetailsRef: RefObject<HTMLDivElement>;
  displayJob: any; // DisplayJob type
}

export function useSectionNavigation({
  overviewRef,
  companyRef,
  jobDetailsRef,
  displayJob,
}: UseSectionNavigationOptions) {
  const [activeSection, setActiveSection] = useState<'overview' | 'company' | 'jobDetails'>('overview');

  // Scroll to section when tab is clicked
  const handleScrollToSection = (section: 'overview' | 'company' | 'jobDetails') => {
    const isDebugEnabled = typeof window !== 'undefined' &&
      (localStorage.getItem('__DEBUG_RENDER__') === 'true' ||
        new URLSearchParams(window.location.search).has('debug-render'));

    if (isDebugEnabled) {
      console.log('[NAV] handleScrollToSection called', {
        section,
        previousSection: activeSection,
        timestamp: new Date().toISOString(),
      });
    }

    setActiveSection(section);

    // Use a small delay to ensure DOM is ready
    setTimeout(() => {
      // Try ref first, then fallback to ID selector, then data-section
      const ref = section === 'overview' ? overviewRef : section === 'company' ? companyRef : jobDetailsRef;
      let element: HTMLElement | null = ref.current;

      if (!element) {
        element = document.querySelector(`#${section}`) as HTMLElement | null;
      }

      if (!element) {
        element = document.querySelector(`[data-section="${section}"]`) as HTMLElement | null;
      }

      if (isDebugEnabled) {
        console.log('[NAV] Element lookup for scroll', {
          section,
          elementFound: !!element,
          elementId: element?.id,
          elementTag: element?.tagName,
        });
      }

      if (element) {
        if (isDebugEnabled) {
          console.log('[NAV] Calling scrollToSection', {
            section,
            offset: 20,
            elementTop: element.getBoundingClientRect().top,
          });
        }
        scrollToSection(section, 20);
      } else {
        if (isDebugEnabled) {
          console.error('[NAV] Could not find element for section', {
            section,
            triedRef: !!ref.current,
            triedIdSelector: !!document.querySelector(`#${section}`),
            triedDataSection: !!document.querySelector(`[data-section="${section}"]`),
          });
        }
      }
    }, 50);
  };

  // Handle hash changes on page load and when hash changes
  useEffect(() => {
    if (!displayJob) return; // Don't run until job is loaded

    const isDebugEnabled = typeof window !== 'undefined' &&
      (localStorage.getItem('__DEBUG_RENDER__') === 'true' ||
        new URLSearchParams(window.location.search).has('debug-render'));

    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the #

      if (isDebugEnabled) {
        console.log('[NAV] Hash change detected', {
          hash,
          fullHash: window.location.hash,
          timestamp: new Date().toISOString(),
        });
      }

      // Sync active section from hash only; browser handles scroll via # links
      if (hash === 'overview' || hash === 'company' || hash === 'jobDetails') {
        if (isDebugEnabled) {
          console.log('[NAV] Setting active section from hash', { section: hash, previousSection: activeSection });
        }
        setActiveSection(hash);
      }
    };

    // Sync initial hash on page load (browser already scrolls to hash; we just set state)
    if (window.location.hash) {
      if (isDebugEnabled) {
        console.log('[NAV] Initial hash on page load', {
          hash: window.location.hash,
          timestamp: new Date().toISOString(),
        });
      }
      handleHashChange();
    }

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [displayJob, activeSection, overviewRef, companyRef, jobDetailsRef]);

  // Update active section based on scroll position
  useEffect(() => {
    const isDebugEnabled = typeof window !== 'undefined' &&
      (localStorage.getItem('__DEBUG_RENDER__') === 'true' ||
        new URLSearchParams(window.location.search).has('debug-render'));

    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          let newSection: 'overview' | 'company' | 'jobDetails' | null = null;

          if (entry.target === overviewRef.current) {
            newSection = 'overview';
          } else if (entry.target === companyRef.current) {
            newSection = 'company';
          } else if (entry.target === jobDetailsRef.current) {
            newSection = 'jobDetails';
          }

          if (newSection && newSection !== activeSection) {
            if (isDebugEnabled) {
              console.log('[NAV] Active section changed via scroll observer', {
                newSection,
                previousSection: activeSection,
                intersectionRatio: entry.intersectionRatio,
                timestamp: new Date().toISOString(),
              });
            }
            setActiveSection(newSection);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    if (overviewRef.current) observer.observe(overviewRef.current);
    if (companyRef.current) observer.observe(companyRef.current);
    if (jobDetailsRef.current) observer.observe(jobDetailsRef.current);

    return () => {
      observer.disconnect();
    };
  }, [displayJob, activeSection, overviewRef, companyRef, jobDetailsRef]);

  return {
    activeSection,
    handleScrollToSection,
  };
}
